import { useState } from 'react';
import { X, Phone, User, MessageSquare, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    issue: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: formData.name,
        phone: formData.phone,
        issue: formData.issue || null,
        source: 'website_form'
      });
      
      setSuccess(true);
      toast.success('Appointment request submitted successfully!');
      
      // Reset after showing success
      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: '', phone: '', issue: '' });
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit appointment:', error);
      toast.error('Failed to submit. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', phone: '', issue: '' });
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] text-2xl text-[#2D4A3E]">
            Book Appointment
          </DialogTitle>
          <DialogDescription className="text-[#6B6B6B]">
            Fill in your details and we'll get back to you shortly.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center" data-testid="booking-success">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#2D4A3E] mb-2">
              Request Submitted!
            </h3>
            <p className="text-[#6B6B6B]">
              We'll contact you soon to confirm your appointment.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#2D4A3E] font-medium">
                Full Name *
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10 border-[#E6E2D8] focus:border-[#2D4A3E] focus:ring-[#2D4A3E]"
                  data-testid="booking-name-input"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#2D4A3E] font-medium">
                Phone Number *
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+91 99866 71810"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 border-[#E6E2D8] focus:border-[#2D4A3E] focus:ring-[#2D4A3E]"
                  data-testid="booking-phone-input"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Issue Field */}
            <div className="space-y-2">
              <Label htmlFor="issue" className="text-[#2D4A3E] font-medium">
                Health Concern (Optional)
              </Label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-[#6B6B6B]" />
                <Textarea
                  id="issue"
                  name="issue"
                  placeholder="Briefly describe your health concern..."
                  value={formData.issue}
                  onChange={handleChange}
                  className="pl-10 min-h-[100px] border-[#E6E2D8] focus:border-[#2D4A3E] focus:ring-[#2D4A3E]"
                  data-testid="booking-issue-input"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2D4A3E] hover:bg-[#1e3329] text-white py-3 rounded-full font-medium"
              data-testid="booking-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>

            {/* Alternative */}
            <p className="text-center text-sm text-[#6B6B6B]">
              Or call us directly at{' '}
              <a
                href="tel:+919986671810"
                className="text-[#C67D63] font-medium hover:underline"
              >
                +91 99866 71810
              </a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
